import { format } from "date-fns";
import getDate from "date-fns/getDate";
import { hi } from "date-fns/locale";
import parse from "date-fns/parse";
import React, { useEffect } from "react";
import {
  Pressable,
  RefreshControl,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  View,
} from "react-native";
import Image from "@components/common/Image";
import LinearGradientView from "react-native-linear-gradient";
import { useDispatch, useSelector } from "react-redux";
import { actions as lglActions } from "@reducers/lglReducer";
import t from "@constants/localization";
import Caret from "../../assets/svg/Caret";
import Lock from "../../assets/svg/Lock";
import BackButton from "../../components/BackButton";
import BlackListedModal from "../../components/LGL/BlackListedModal";
import PieStat from "../../components/LGL/PieStat";
import PreviousMonthStats from "../../components/LGL/PreviousMonthStats";
import Rewards from "../../components/LGL/Rewards";
import { LGL } from "../../constants/analyticsEventsCT";
import color from "../../constants/color";
import analytics from "../../services/analytics";
import toast from "../../utils/toast";

const PATH = "screens/LGL/index";

const BackImage = () => (
  <View style={{ padding: 8 }}>
    <Image
      source={require("../../assets/images/back_button_bold.webp")}
      style={{ width: 18, height: 15 }}
    />
  </View>
);

const boldNumbersinString = (str) =>
  str
    .split(" ")
    .map((item) =>
      isNaN(item.trim()) ? (
        <Text
          style={{ fontFamily: "NotoSansDevanagari-Regular" }}
        >{`${item} `}</Text>
      ) : (
        <Text
          style={{ fontFamily: "NotoSansDevanagari-Bold" }}
        >{`${item} `}</Text>
      ),
    );

const HeaderWithBackAndTitle = ({
  navigation,
  name = "",
  currentLevel = {},
  widget = {},
}) => (
  <View
    style={{
      paddingVertical: 13,
      flexDirection: "row",
      alignItems: "center",
    }}
  >
    <BackButton
      tintColor="white"
      onPress={navigation.goBack}
      backImage={BackImage}
    />
    <Text
      style={{
        fontFamily: "NotoSansDevanagari-Bold",
        color: "white",
        fontSize: 20,
        marginLeft: 20,
      }}
    >
      {name}
    </Text>
    {currentLevel?.priority > 0 && (
      <Pressable
        android_ripple={{
          color: "rgba(0,0,0,0.08)",
          borderless: false,
        }}
        onPress={() => {
          navigation.navigate("LeaderLadderDetails", {
            data: currentLevel,
            locked: false,
            widget,
          });
        }}
        style={{
          flexDirection: "row",
          alignItems: "center",
          marginLeft: "auto",
          paddingHorizontal: 15,
          paddingVertical: 6,
        }}
      >
        <Text
          style={{
            color: "white",
            fontFamily: "NotoSansDevanagari-Bold",
            fontSize: 16,
          }}
        >
          {t(`${PATH}/viewDetails`)}
        </Text>
        <Caret strokeColor="white" />
      </Pressable>
    )}
  </View>
);

const getFormattedDate = (dateString) => {
  if (!dateString) return "";
  const parsedDate = parse(dateString, "yyyy-MM-dd", new Date());
  return `${format(parsedDate, "LLLL", { locale: hi })} ${getDate(parsedDate)}`;
};

const LeaderGrowthLadder = ({ navigation }) => {
  const data = useSelector((state) => state.lgl.data) || {};
  const error = useSelector((state) => state.lgl.error) || "";
  const dispatch = useDispatch();
  const {
    currentLevel = {},
    nextLevelToShow = {},
    inCompleteLevels = {},
    widget = {},
    blacklisted = false,
    headerSubTitle = null,
  } = data || {};

  const onRefreshing = () => {
    dispatch(lglActions.getLGL());
  };

  useEffect(() => {
    if (typeof data === "object" && !Object.keys(data).length) {
      dispatch(lglActions.getLGL());
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    analytics.trackEvent(LGL.LGL_LANDING, currentLevel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    toast.showShortToast(error);
    navigation.goBack();
    return <></>;
  }

  const renderLevelValidityDate = () => {
    if (!currentLevel?.config_end_date) {
      return "";
    }
    return getFormattedDate(currentLevel.config_end_date);
  };

  return (
    <View style={{ flex: 1, backgroundColor: '#E61739' }}>
      <StatusBar barStyle="light-content" />
      <ScrollView
        refreshControl={
          <RefreshControl refreshing={false} onRefresh={onRefreshing} />
        }
      >
        <LinearGradientView
          colors={["#FF4D4D", "#E61739"]}
          angle={93}
          useAngle={true}
        >
          <HeaderWithBackAndTitle
            navigation={navigation}
            name={currentLevel?.name}
            currentLevel={currentLevel}
            widget={widget}
          />
          <View
            style={{
              justifyContent: "center",
              alignItems: "center",
              marginTop: "auto",
              marginBottom: 10,
            }}
          >
            <View
              style={{
                marginBottom: 14,
              }}
            >
              <Image
                source={{ uri: currentLevel.icon }}
                style={{ height: 80, width: 80 }}
              />
            </View>
            {typeof headerSubTitle === "string" && !!headerSubTitle.length ? (
              <Text
                style={{
                  color: "white",
                  fontFamily: "NotoSansDevanagari-Regular",
                  fontSize: 16,
                }}
              >
                {boldNumbersinString(headerSubTitle)}
              </Text>
            ) : null}

            <Text
              style={{
                color: "rgba(255,255,255,0.7)",
                fontFamily: "NotoSansDevanagari-Bold",
              }}
            >
              {currentLevel.name} {renderLevelValidityDate()} तक
            </Text>
          </View>
        </LinearGradientView>
        <View style={styles.body}>
          <NextLevelToShow
            nextLevelToShow={nextLevelToShow}
            navigation={navigation}
            validTill={renderLevelValidityDate()}
          />

          <View
            style={{
              marginVertical: nextLevelToShow.name ? 40 : 0,
              paddingHorizontal: 15,
            }}
          >
            {nextLevelToShow.name ? (
              <View
                style={{
                  justifyContent: "space-between",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <Text
                  style={{
                    fontFamily: "NotoSansDevanagari-Bold",
                    fontSize: 16,
                  }}
                >
                  {nextLevelToShow?.name} {t(`${PATH}/nextLevelsProfits`)}
                </Text>

                <Pressable
                  style={{ flexDirection: "row" }}
                  onPress={() => {
                    navigation.navigate("LeaderLadderDetails", {
                      data: nextLevelToShow,
                      widget,
                    });
                    analytics.trackEvent(LGL.LGL_MODULE_CLICKED, {
                      module_name: nextLevelToShow?.name,
                    });
                  }}
                  android_ripple={{
                    color: "rgba(0,0,0,0.08)",
                    borderless: false,
                  }}
                >
                  <Text
                    style={{
                      color: color.primary,
                      fontFamily: "NotoSansDevanagari-Bold",
                    }}
                  >
                    {t(`${PATH}/viewDetails`)}
                  </Text>

                  <Caret strokeColor={color.primary} />
                </Pressable>
              </View>
            ) : null}
            <Rewards rewards={nextLevelToShow?.rewards} />

            <PreviousMonthStats widget={widget} />
          </View>
          {Array.isArray(inCompleteLevels) && !!inCompleteLevels.length ? (
            <LevelLadder
              nextLevelToShow={nextLevelToShow}
              inCompleteLevels={inCompleteLevels}
              navigation={navigation}
            />
          ) : null}
        </View>
      </ScrollView>

      <BlackListedModal blacklisted={blacklisted} navigation={navigation} />
    </View>
  );
};

const NextLevelToShow = ({ nextLevelToShow, navigation, validTill = "" }) => {
  const { name = "", eligibility = [] } = nextLevelToShow || {};
  if (!name) return null;
  return (
    <>
      <Pressable
        android_ripple={{ color: "rgba(0,0,0,0.08)", borderless: false }}
        style={{
          paddingVertical: 20,
          paddingHorizontal: 15,
          backgroundColor: "white",
        }}
        onPress={() => {
          navigation.navigate("LeaderLadderDetails", { data: nextLevelToShow });

          analytics.trackEvent(LGL.LGL_MODULE_CLICKED, {
            module_name: name,
          });
        }}
      >
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Text
            style={{
              fontFamily: "NotoSansDevanagari-Bold",
              fontSize: 20,
              color: "rgba(0,0,0,0.84)",
            }}
          >
            {t(`${PATH}/unlock`)} {name}
          </Text>
          <Caret strokeColor={color.primary} />
        </View>
        <Text>
          {t(`${PATH}/timeLeftToResetTarget`)}
          <Text style={{ fontFamily: "NotoSansDevanagari-Bold" }}>
            {validTill}
          </Text>
        </Text>
      </Pressable>
      <View
        style={{
          flexDirection: "row",
          flexWrap: "wrap",
          justifyContent: "space-between",
          paddingHorizontal: 15,
          paddingBottom: 5,
          backgroundColor: "white",
        }}
      >
        {eligibility.map((item) => (
          <PieStat
            key={item.id}
            percentage={(item.current_state * 100) / item.target}
            title={item.title_text_template}
            subTitle={item.sub_title_text_template}
          />
        ))}
      </View>
    </>
  );
};

const LevelLadder = (props) => {
  const { inCompleteLevels, navigation } = props;
  const nextLevelToShow =
    Array.isArray(inCompleteLevels) && inCompleteLevels.length
      ? inCompleteLevels[1]
      : {};

  const { name, rewards = [] } = nextLevelToShow || {};
  return (
    <View style={{}}>
      <Pressable
        style={{
          paddingHorizontal: 15,
          paddingTop: 15,
          backgroundColor: "white",
        }}
        android_ripple={{ color: "rgba(0,0,0,0.08)", borderless: false }}
        onPress={() => {
          navigation.navigate("LeaderLadderDetails", { data: nextLevelToShow });
          analytics.trackEvent(LGL.LGL_MODULE_CLICKED, {
            module_name: name,
          });
        }}
      >
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Text
              style={{
                fontFamily: "NotoSansDevanagari-Bold",
                fontSize: 20,
                color: "rgba(0,0,0,0.84)",
                marginRight: 10,
              }}
            >
              {name}
            </Text>
            <Lock />
          </View>
          <Caret strokeColor={color.primary} />
        </View>
        <Text>{t(`${PATH}/timeLeftToResetTarget`, { name })}</Text>
      </Pressable>

      <View
        style={{
          paddingHorizontal: 15,
          paddingBottom: 10,
          backgroundColor: "white",
        }}
      >
        <Rewards rewards={rewards} disabled={true} />
      </View>
      <View style={{ marginTop: 15 }}>
        {Array.isArray(inCompleteLevels) &&
          inCompleteLevels
            .slice(2)
            .map((level) => <LadderCard {...level} navigation={navigation} />)}
      </View>
    </View>
  );
};

const LadderCard = (props) => {
  const { name, navigation } = props;
  return (
    <View
      style={{
        backgroundColor: "white",
        paddingVertical: 20,
        paddingHorizontal: 15,
        marginVertical: 5,
      }}
    >
      <View style={{ justifyContent: "space-between", flexDirection: "row" }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Text
            style={{
              fontFamily: "NotoSansDevanagari-Bold",
              fontSize: 20,
              marginBottom: 7,
              marginRight: 13,
            }}
          >
            {name}
          </Text>
          <Lock />
        </View>
        <Pressable
          onPress={() => {
            navigation.navigate("LeaderLadderDetails", { data: props });
            analytics.trackEvent(LGL.LGL_MODULE_CLICKED, {
              module_name: name,
            });
          }}
          style={{
            flexDirection: "row",
            alignItems: "center",
            paddingLeft: 10,
          }}
          android_ripple={{
            color: "rgba(0,0,0,0.08)",
            borderless: false,
          }}
        >
          <Text
            style={{
              color: color.primary,
              fontFamily: "NotoSansDevanagari-Bold",
            }}
          >
            {t(`${PATH}/viewDetails`)}
          </Text>
          <Caret strokeColor={color.primary} />
        </Pressable>
      </View>
      <Text>{t(`${PATH}/unlockNextLevel`, { name })}</Text>
    </View>
  );
};

export default LeaderGrowthLadder;
const styles = StyleSheet.create({
  body: {
    backgroundColor: "#F7F7F7",
    paddingBottom: 15,
  },
});
